/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-12 15:40:50                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-25 16:02:48                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * DetailsDrawer: display details drawer
*/

/* Imports */
import { Button, Card, CardBody, CardFooter, Typography } from "@material-tailwind/react";
import { Modal } from "@mui/material";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { showApiErrorToast } from "../../../services/api/error";
import { CalendarIcon, XMarkIcon } from "@heroicons/react/24/solid";
/***/

/* Components */
import CustomerDisplay from "../../../components/data/customer/CustomerDisplay";
import AddressDisplay from "../../../components/data/address/AddressDisplay";
import DateDisplay from "../../../components/data/date/DateDisplay";
import TechnicianSelect from "../../../components/data/technician/TechnicianSelect";
import TechnicianDisplay from "../../../components/data/technician/TechnicianDisplay";
import WorkordersStatusDisplay from "../../../components/data/workorder-status/WorkordersStatusDisplay";
import DateRangeSelect from "../../../components/data/date-range/DateRangeSelect";
import { WorkorderStatus } from "../../../types/workorder/workorderStatus";
/***/

/* Services */
import { api } from "../../../services/api/api";
/***/

import "./PlanningModal.scss";
import TimeTypeSelect from "../../../components/data/time-type/TimeTypeSelect";
import Loading from "../../../components/loading/Loading";

/**
* DetailsDrawer: display details drawer
* @param time - time to display
* @param onUpdate - on update callback, return updated time
*/
export default function TimeEditModal({
  open,
  time,
  onUpdate,
  onDelete,
  onClose
}: {
  open: boolean,
  time: EasyTech.Time,
  onUpdate: (time: EasyTech.Time) => void,
  onDelete: (time: EasyTech.Time) => void,
  onClose: () => void
}) {
  const { t } = useTranslation();
  const [onEdit, setOnEdit] = useState<string | null>(null);
  const [tech, setTech] = useState<EasyTech.Technician | null>(null);
  const [type, setType] = useState<EasyTech.TimeType | null>(null);
  const [schedule, setSchedule] = useState<{start: string, end: string | null}>({start: time.date_from, end: time.date_to});
  const [loadingState, setLoadingState] = useState<boolean>(false);

  /**
  * save: Save
  */
  const save = (): void => {
    saveEditTime();
  };
  /***/

  /**
   * saveEditTime: Sauvegarde les modifications d'un technician_time
   */
  const saveEditTime = (): void => {
    setLoadingState(true);
    
    api.put(`admin/technician_times/${time.id}`, {
      ...time,
      technician: (onEdit === "woTech" || onEdit === "time") && tech ? tech : time.technician,
      type: onEdit === "time" && type ? type : time.type,
      date_from: onEdit === "schedule" && schedule ? schedule.start : time.date_from,
      date_to: onEdit === "schedule" && schedule ? (schedule.end || time.date_to) : time.date_to
    }).then((ret) => {
      onUpdate({...ret as EasyTech.Time, technician: tech ? tech : time.technician});
      setOnEdit(null);
      toast.success(t("planification:timeEdited"));
    }).catch((err) => {
      console.error(err);
      showApiErrorToast(err);
    }).finally(() => {
      setLoadingState(false);
      onClose();
    });
  };
  /***/

  /**
   * deleteTime: Supprime un technician_time
   */
  const deleteTime = () => {
    setLoadingState(true);
    
    api.delete(`admin/technician_times/${time.id}`).then(() => {
      onDelete(time);
      setOnEdit(null);
      toast.success(t("planification:timeDeleted"));
    }).catch((err) => {
      console.error(err);
      showApiErrorToast(err);
    }).finally(() => {
      setLoadingState(false);
      onClose();
    });
  };

  return(
    
    <Modal
      open={open}
      onClose={onClose}
      className="te-modal"
      aria-labelledby="te-modal"
      aria-describedby="te-modal">
      <Card className="te-modal-content  rounded-lg">
        <button className="close-button" disabled={loadingState} onClick={() => onClose()}>
          <XMarkIcon className="w-4"/>
        </button>
        <CardBody>
        {time.type.label === "Intervention" &&
          <div>
            <div  className="flex items-center gap-3 mb-5">
              <div className="rounded-full border p-2 bg-light-green-200">
                <CalendarIcon className="w-7 text-green-700" /> 
              </div>
                <Typography variant="h5">
                  {t("planification:scheduleIntervention")} <a href={"/workorders/"+time.workOrder?.id}>{time.workOrder?.ref}</a>
                </Typography>
            </div>

            <div className="flex justify-between">
              <TechnicianDisplay technician={time.technician}/>
              <DateDisplay date={new Date(time.date_from)}/>
            </div>

            <div className="flex justify-between">
              <CustomerDisplay customer={time.workOrder?.customer as EasyTech.Customer}/>
              <WorkordersStatusDisplay status={time.workOrder?.status}/>
            </div>
            {time.workOrder &&
              <AddressDisplay address={time.workOrder?.address} zone={time.workOrder?.zone} />
            }
          </div>
        }

        {time.type.label !== "Intervention" &&
          <div>
            <div  className="flex items-center gap-3 mb-5">
              <div className="rounded-full border p-2 bg-light-green-200">
                <CalendarIcon className="w-7 text-green-700" /> 
              </div>
                <Typography variant="h5">
                  {t("planification:scheduleTechnicianUnavailability")}
                </Typography>
            </div>

            <div className="flex justify-between">
              <TechnicianDisplay technician={time.technician}/>
              <DateDisplay date={new Date(time.date_from)}/>
            </div>

            <div>
              <Typography>{time.type.label}</Typography>
            </div>   

           
          </div>
        }

        {(onEdit === "woTech" && time.workOrder) &&
          <div className="py-4">
            <TechnicianSelect onChange={(el) => setTech(el)}
                              value={time.technician as any}
                              address={time.workOrder.address}
                              schedule={time.workOrder.schedule}
                              isDetailed={true}/>
          </div>
        }
        {onEdit === "time" && <>
          <div className="py-4">
            <TimeTypeSelect value={time.type} onChange={(el) => setType(el)}/>
          </div>
          <div className="py-4">
            <TechnicianSelect value={time.technician as any} onChange={(el) => setTech(el)}/>
          </div>
        </>
        }
        {onEdit === "schedule" &&
          <div className="py-2">
            <DateRangeSelect  range={schedule}
                              onChange={el => setSchedule(el)}/>
          </div>
        }
        {onEdit === "delete" &&
          <div className="py-2">
            <Typography>{t("planification:areYouSureToDeleteThisTimePermanently")}</Typography>
          </div>
        }
          
        </CardBody>
        <CardFooter className="pt-0 flex gap-2 justify-end">
          {!onEdit ?
            <>
              <Button color="blue"
                      disabled={time.workOrder?.status === WorkorderStatus.IN_PROGRESS}
                      onClick={() => setOnEdit("schedule")}>
                {t("planification:reSchedule")}
              </Button>

              {time.type.label !== "Intervention" &&
                <Button color="blue"
                      onClick={() => setOnEdit("time")}>
                {t("planification:reAssign")}
               </Button>
              }

              {time.type.label !== "Intervention" &&
                <Button color="red"
                      onClick={() => setOnEdit("delete")}>
                {t("planification:delete")}
               </Button>
              }

              {time.type.label === "Intervention" &&
                <Button color="blue"
                        disabled={time.workOrder?.status === WorkorderStatus.IN_PROGRESS}
                        onClick={() => setOnEdit("woTech")}>
                  {t("planification:reAssign")}
                </Button>
              }
            </>
          : 
            <>
              <Button variant="outlined"
                      disabled={loadingState}
                      onClick={() => setOnEdit(null)}>
                {t("planification:cancel")}
              </Button>

              {onEdit === "delete" ?
                <Button color="blue"
                disabled={loadingState}
                onClick={() => deleteTime()}>
                  {t("planification:validate")}
                </Button>
              :
                <Button color="blue"
                        disabled={loadingState}
                        onClick={() => save()}>
                  {loadingState ?
                    <Loading size="h-4 w-4"/>
                  :
                    t("planification:save")
                  }
                </Button>
              }
            </>
          }
        </CardFooter>
      </Card>
    </Modal>
  );
}
/***/
